import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { HttpErrorResponse } from "@angular/common/http";
import { map, mergeMap } from 'rxjs/operators';

import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { applicationConstants } from "../../../../app.constants";
import { Events, EventBusService } from "../../../../technical-modules/events/services/event-bus.service";

import { PlanPreventionService } from '../../services/plan-prevention.service';
import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';

import { SignatairePdpEntity } from '../../entities/signataire-pdp.entity';
import { PlanPreventionEntity } from '../../entities/plan-prevention.entity';
import { PdpSignatairesUpdate } from '../../entities/pdp-signataires-update.entity';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';
import { IntervenantForSignataireUpdate } from '../../entities/intervenant-for-signataire-update.entity';

type SignatairesParEntreprise = {
	[id: string]: {
		'nomEntreprise': string,
		'signataires': SignatairePdpEntity[],
		'checkAllForDocusign': boolean
	};
}

@Component({
	selector: 'gestion-signature-pdp-specifique',
	templateUrl: 'gestion-signature-pdp-specifique.html',
	styleUrls: ['gestion-signature-pdp-specifique.css'],
})
export class GestionSignaturePdpSpecifiqueComponent {
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();
	pdp: PlanPreventionEntity = new PlanPreventionEntity();
	
	// Valeurs "statiques" a bind sur la vue
	entreprises: EntrepriseTLR[] = [];
	entrepriseUtilisatrice!: EntrepriseTLR | null;
	signatairesActuels: SignatairePdpEntity[] = [];
	referentielSignataires: SignatairesParEntreprise = {};
	
	// Valeurs saisissables a bind sur la vue
	allSendDocusign: boolean = false;
	selectedEntrepriseSignataires!: EntrepriseTLR;
	allSignatairesSigned: boolean = false;
	waitingForServer: boolean = false;
	hasAtLeastOneSigner: boolean = false;
	valideursCanSignThisPdp: boolean = false;
	envelopeIds: string[] = [];
	
	// Gestion de la fenetre de dialogue d'ajout de signataires
	editSignatairesCommands = new Subject<[SignatairePdpEntity[], SignatairePdpEntity[]]>();
	private signatairesProposables: SignatairePdpEntity[] = [];
	
	viewEnveloppeCommand = new Subject<string>();
	
	constructor( //
		@Inject(EventBusService) private eventBusService: EventBusService,
		@Inject(IntervenantService) intervenantService: IntervenantService,
		@Inject(PlanPreventionService) private planPreventionService: PlanPreventionService,
	) {
		// Lors de la reception d'une demande de signature de pdp
		this.signCommandsSubject.pipe(
			// Charger les signataires actuels et les entreprises
			mergeMap(
				(pdp) => {
					console.log('gestion des signature pour un pdp specifique');
					this.entreprises = [];
					let signataires$ = planPreventionService.getSignataires(pdp.id);
					let entreprises$ = planPreventionService.getTravaux(pdp.id).pipe(
						map(
							(pdpTravaux) => pdpTravaux.entreprisesExterieures.map(
								(ee) => {
									let entTlr = new EntrepriseTLR();
									entTlr.id = ee.idEntrepriseExt;
									entTlr.raisonSociale = ee.raisonSociale;
									entTlr.nom = ee.nomEntreprise;
									return entTlr;
								}
							)
						)
					);
					let referentParc$ = intervenantService.getIntervenantById(pdp.referentParcId);
					return combineLatest([of(pdp), signataires$, entreprises$, referentParc$]);
				}
			),
			// Puis charger les charges d'acces lies aux entreprises
			mergeMap(
				(data) => {
					let entreprises = data[2];
					let intervenants$ = (!entreprises || !entreprises.length) ?
						of([]) :
						intervenantService.getIntervenantsForEntreprise(entreprises.map($ => $.id)).pipe(
							map(
								(intervenants) => intervenants.map(
									(intervenant) => {
										let signataire: SignatairePdpEntity = {
											id: intervenant.id,
											nom: intervenant.nom,
											prenom: intervenant.prenom,
											nomEntreprise: intervenant.nomEntreprise,
											raisonSociale: '',
											idEntreprise: intervenant.idEntreprise,
											fonction: intervenant.cadreEmploi,
											hasSigned: false,
											hasSignedEnvelope: false,
											email: intervenant.email,
											sentToDocusign: false,
											emailStyle: intervenant.email && intervenant.email.trim() && applicationConstants.emailRegexp.test(intervenant.email) ? '' : 'error',
											envelopeId: '',
											envelopeIndex: -1,
											nature: 'intervenant',
											isIntervenant: true,
											updatable: true,
											docusignRecipientId: ''
										}
										return signataire;										
									}
								)
							)
						)
					;

					// charger les intervenants de l'entreprise utilisatrice
					let intervenantsInterne$ = intervenantService.getIntervenantsForEntreprise([data[3].idEntreprise]).pipe(
						map(
							(intervenants) => intervenants.map(
								(intervenant) => {
									let signataire: SignatairePdpEntity = {
										id: intervenant.id,
										nom: intervenant.nom,
										prenom: intervenant.prenom,
										nomEntreprise: intervenant.nomEntreprise,
										raisonSociale: '',
										idEntreprise: intervenant.idEntreprise,
										fonction: intervenant.cadreEmploi,
										hasSigned: false,
										hasSignedEnvelope: false,
										email: intervenant.email,
										sentToDocusign: false,
										emailStyle: intervenant.email && intervenant.email.trim() && applicationConstants.emailRegexp.test(intervenant.email) ? '' : 'error',
										envelopeId: '',
										envelopeIndex: -1,
										nature: 'intervenant',
										isIntervenant: true,
										updatable: true,
										docusignRecipientId: ''
									}
									return signataire;	
								} )
							)
						);
						this.entrepriseUtilisatrice = new EntrepriseTLR();
						this.entrepriseUtilisatrice.id = data[3].idEntreprise;
						this.entrepriseUtilisatrice.nom = data[3].nomEntreprise;
						this.entreprises.push(this.entrepriseUtilisatrice);
					
						return combineLatest([of(data[0]), of(data[1]), of(data[2]), intervenants$, intervenantsInterne$]);
				}
 			)
		).subscribe(
			// Puis afficher la fenetre
			(data) => {
				this.hasAtLeastOneSigner = false;
				this.waitingForServer = false;
				this.pdp = data[0];
				this.valideursCanSignThisPdp = data[1].versionTechnique >= applicationConstants.versionInWhichValideursCanSignPdps && data[1].typePdp.toString() === 'GENERAL';
				let signataires = data[1].signataires;
				signataires.forEach ( $ => $.updatable = $.envelopeId ? false : true );
				this.signatairesActuels = signataires.sort( ($1, $2) => $1.nom.localeCompare($2.nom) );
				this.envelopeIds = this.signatairesActuels.map( signataire => signataire.envelopeId )
					.filter( $ => $ != null && $!= '' ) // garder les valeurs non null
					.filter( (value, index, array) => array.indexOf(value) === index ) // garder les valeur uniques
					.sort( ($1, $2) => $1.localeCompare($2) );
				this.classerSignataires();
				this.updateAllSignedCheckBox();
				this.updateAllDocusignCheckBox();
				this.entreprises = this.entreprises.concat(data[2]);
				// enlever les entreprises en doublon éventuellement
				this.entreprises = this.entreprises.filter((item, index, self) => self.findIndex((item2) => item.id === item2.id) === index);
				// trier la liste des entreprises par nom
				this.entreprises.sort(
					(e1, e2) => e1.nom.localeCompare(e2.nom)
				);
				this.signatairesProposables = data[3];
				this.signatairesProposables = this.signatairesProposables.concat(data[4]);
				this.signatairesProposables.sort(
					(s1, s2) => s1.nomEntreprise.localeCompare(s2.nomEntreprise) || s1.nom.localeCompare(s2.nom)
				);
				this.displaySubject.next(true);
			}
 		);
	}

	@Input() id = 'pdpSignerDialog';
	
	// Liaison de l'observable servant a commander l'affichage du composant
	private signCommandsSubject = new SubjectRelay<PlanPreventionEntity>();
	@Input() set signCommands(obs: Observable<PlanPreventionEntity> | undefined) {
		this.signCommandsSubject.registerSource(obs);
	}

	// Evenements de changement sur le pdp
	@Output() pdpChanged = new EventEmitter<number>();
	
	private updateAllSignedCheckBox() {
		if (this.signatairesActuels && this.signatairesActuels.length) {
			this.allSignatairesSigned = Object.keys(this.referentielSignataires).every(
				entrepriseId => this.referentielSignataires[entrepriseId].signataires.every(signataire => signataire.hasSigned) 
			);
		} else {
			this.allSignatairesSigned = false;
		}
	}
	
	private updateAllDocusignCheckBox() {
		if (this.signatairesActuels && this.signatairesActuels.length) {
			this.allSendDocusign = Object.keys(this.referentielSignataires).every(
				entrepriseId => this.referentielSignataires[entrepriseId].signataires
					.filter( signataire => signataire.email && signataire.email.trim() )
					.every( signataire => signataire.sentToDocusign )
			);
		} else {
			this.allSendDocusign = false;
		}
	}
	
	private updateAllDocuForEntreprise(entrepriseId: string) {
		if (this.referentielSignataires[entrepriseId]) {
			this.referentielSignataires[entrepriseId].checkAllForDocusign = 
				this.referentielSignataires[entrepriseId].signataires.every( signataire => signataire.sentToDocusign );
		}
	}

	openDialogSignataires() {
		// Si une entreprise exterieure est selectionnee, on ne proposera que les charges d'acces de cette entrepise, sinon tous les charges d'acces de toutes les entreprises exterieures du pdp
		let signatairesProposables = this.selectedEntrepriseSignataires ?
			this.signatairesProposables.filter( signataireExt => signataireExt.idEntreprise === this.selectedEntrepriseSignataires.id )
			: this.signatairesProposables
		;
		
		// Determiner l'ensemble de ces signataires a proposer a l'utilisateur qui ne sont pas actuellement inclus dans le pdp
		let signatairesAjoutables = signatairesProposables.filter($1 => this.signatairesActuels.every($2 => $1.id !== $2.id));
		
		this.signatairesActuels.forEach(signatairesActuel => {
			// pour chaque signataire renseigner son etat hasSigned, sentToDocusign, et emailStyle
			// car sinon au retour on va le perdre
			Object.keys(this.referentielSignataires).forEach(
				entrepriseId => this.referentielSignataires[entrepriseId].signataires
					.forEach( signataire => {
						if ( signataire.id === signatairesActuel.id ) {
							signatairesActuel.hasSigned = signataire.hasSigned;
							signatairesActuel.sentToDocusign = signataire.sentToDocusign;
							signatairesActuel.email = signataire.email;
							signatairesActuel.emailStyle = signataire.emailStyle;
							signatairesActuel.envelopeId = signataire.envelopeId;				
						}
					} )
			);
		});

		this.editSignatairesCommands.next([
			signatairesAjoutables, 
			this.signatairesActuels.sort(
				(s1, s2) => s1.nom.localeCompare(s2.nom)
			)
		])
		;
	}

	onSignatairesSelected(data: [SignatairePdpEntity[], SignatairePdpEntity[]]) {
		let signatairesLies = data[1].sort(($1, $2) => $1.nom.localeCompare($2.nom));
		if (!signatairesLies) {
			this.signatairesActuels = [];
			return;
		}
		
		this.signatairesActuels = signatairesLies.map(
			(value: SignatairePdpEntity) => {
				let entreprise = this.entreprises.find($ => $.id === value.idEntreprise);
				let signataire: SignatairePdpEntity = {
					id: value.id,
					nom: value.nom,
					prenom: value.prenom,
					nomEntreprise: value.nomEntreprise,
					raisonSociale: entreprise ? entreprise.raisonSociale : '',
					idEntreprise: value.idEntreprise,
					fonction: value.fonction,
					hasSigned: value.hasSigned,
					hasSignedEnvelope: value.hasSignedEnvelope,
					email: value.email,
					sentToDocusign: value.sentToDocusign,
					emailStyle: value.emailStyle,
					envelopeId: value.envelopeId,
					envelopeIndex: value.envelopeIndex,
					nature: value.nature,
					isIntervenant: value.isIntervenant,
					updatable: true,
					docusignRecipientId: ''
				};
				return signataire;
			}
		);
		const selectedEntrepriseIds = [...new Set(this.signatairesActuels.map(sig => sig.idEntreprise))];
		selectedEntrepriseIds.forEach( entrepriseId => this.updateAllDocuForEntreprise(entrepriseId.toString()) );
		this.classerSignataires();
		this.updateAllSignedCheckBox();
		this.updateAllDocusignCheckBox();
	}
	
	onSignataireDialogClosed() {
	}
	
	onChangeToutesSignatures(value: boolean) {
		Object.keys(this.referentielSignataires).forEach(
			entrepriseId => {
				this.referentielSignataires[entrepriseId].signataires.forEach( signataire => {
					if (!signataire.envelopeId || !signataire.hasSigned) {
						signataire.hasSigned = value;
					}
				} );
			}
		);
	}
	
	onChangeToutesDocusign(value: boolean) {
		Object.keys(this.referentielSignataires).forEach(
			entrepriseId => {
				this.referentielSignataires[entrepriseId].signataires.forEach(signataire => {
					if (signataire.email && signataire.email.trim() && applicationConstants.emailRegexp.test(signataire.email) && !signataire.envelopeId) {
						signataire.sentToDocusign = value;
					}
				});
				this.referentielSignataires[entrepriseId].checkAllForDocusign = value;
			}
		);
		this.updateAllDocusignCheckBox();
		this.checkIfShouldDocusign();
	}
	
	onChangeToutesDocuForEntreprise(value: boolean, entrepriseId: string) {
		this.referentielSignataires[entrepriseId].signataires.forEach(signataire => {
			if (signataire.email && signataire.email.trim() && applicationConstants.emailRegexp.test(signataire.email) && !signataire.envelopeId) {
				signataire.sentToDocusign = value;
			}
		});
		this.updateAllDocusignCheckBox();
		this.checkIfShouldDocusign();
	}
		
	onChangeSignature(_signataireId: number, _value: boolean) {
		this.signatairesActuels.filter(
			$ => $.id == _signataireId 
		)[0].hasSigned = _value;
		this.updateAllSignedCheckBox();
	}
	
	onChangeDocuSign(signataire: SignatairePdpEntity, value: boolean) {
		signataire.sentToDocusign = value && signataire.emailStyle !== 'error';
		this.updateAllDocusignCheckBox();
		this.updateAllDocuForEntreprise(signataire.idEntreprise.toString());
		this.checkIfShouldDocusign();
	}
	
	checkIfShouldDocusign(){
		this.hasAtLeastOneSigner = Object.keys(this.referentielSignataires).some(
			entrepriseId => this.referentielSignataires[entrepriseId].signataires
				.filter( signataire => signataire.envelopeId === null || signataire.envelopeId === '' )
				.some( signataire => signataire.sentToDocusign )
		);
	}
	
	onFocusLeaveEmail(signataire: SignatairePdpEntity) {
		if (signataire.email) {
			signataire.email = signataire.email.trim();
			if (!applicationConstants.emailRegexp.test(signataire.email)) {
				console.log('email incorrect: ', signataire.email);
				signataire.sentToDocusign = false;
				signataire.emailStyle = 'error';
			} else {
				signataire.emailStyle = '';
			}
			this.updateAllDocuForEntreprise(signataire.idEntreprise.toString());
			this.updateAllDocusignCheckBox();
			for (let signataireActuel of this.signatairesActuels) {
				if (signataire.id === signataireActuel.id) {
					signataireActuel.email = signataire.email;
					break;
				}
			}
		}
	}
	
	onCloseRequested() {
		this.cleanAndClose();
	}
	
	private cleanAndClose() {
		this.signatairesActuels = [];
		this.entreprises = [];
		this.signatairesProposables = [];
		this.referentielSignataires = {};
		this.displaySubject.next(false);
		this.entrepriseUtilisatrice = null;
		this.pdpChanged.emit(this.pdp.id);
	}
	
	hasInvalidEmails() {
		// on tolere les emails vides mais pas invalides
		let entrepriseIds = Object.keys(this.referentielSignataires);
		for (let i = 0; i < entrepriseIds.length; i++) {
			if (this.referentielSignataires[entrepriseIds[i]]
					.signataires.some($ => $.email !== null && $.email.trim().length != 0 && $.emailStyle === 'error')) {
				return true;
			}
		}
		return false;
	}

	hasBlankOrInvalidEmails() {
		// zero tolerance car pour docusign faut que l'email soit renseigné et au bon format
		let entrepriseIds = Object.keys(this.referentielSignataires);
		for (let i = 0; i < entrepriseIds.length; i++) {
			if (this.referentielSignataires[entrepriseIds[i]]
					.signataires.some($ => ($.emailStyle === 'error' || !$.email || $.email.trim().length === 0) && $.sentToDocusign)) {
				return true;
			}
		}
		return false;
	}

	onSaveRequested() {
		if (this.hasAtLeastOneSigner && 
			!confirm('Une fois l\'enveloppe envoyée, aucune modification ne sera possible.\n' +
			'Pour confirmer l\'envoi, cliquer sur OK.')) {
			return;
		}
		
		let signedIds: number[] = [];
		Object.keys(this.referentielSignataires).forEach(
			entrepriseId => {
				signedIds.push.apply(
					signedIds, this.referentielSignataires[entrepriseId].signataires.filter($ => $.hasSigned).map($ => $.id)
				);
			}
		);
		
		let sendToDocusignIds: number[] = [];
		Object.keys(this.referentielSignataires).forEach(
			entrepriseId => {
				sendToDocusignIds.push.apply(
					sendToDocusignIds, this.referentielSignataires[entrepriseId].signataires.filter($ => $.sentToDocusign).map($ => $.id)
				);
			}
		);
		this.signatairesActuels.forEach( signatairesActuel => {
			if (sendToDocusignIds.indexOf(signatairesActuel.id) > -1) {
				signatairesActuel.sentToDocusign = true;
			}
		} );
		
		let intervenants = this.signatairesActuels.map($ => {
			return {
				id: $.id,
				email: $.email,
				sentToDocusign: $.sentToDocusign,
				envelopeId: $.envelopeId,
				fonction: $.fonction
			} as IntervenantForSignataireUpdate;
		});
		
		let command: PdpSignatairesUpdate = {
			signataires: intervenants,
			signatures: signedIds,
		};
		
		this.waitingForServer = true;
		this.planPreventionService.updateSignataires(this.pdp.id, command, this.valideursCanSignThisPdp).subscribe(
			(result) => {
				this.waitingForServer = false;
				this.eventBusService.emit(Events.RefreshBatchIndicatorRequested, null);
				this.cleanAndClose();
			},
			(httpErrorResponse: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.eventBusService.emit(Events.RefreshBatchIndicatorRequested, null);
				
				let error = httpErrorResponse.error;
				if (error != null) {
					if (error.exceptionClass === 'com.engie.aurora.exceptions.BadRequestException' && error.message) {
						alert(error.message);
						return;
					}
				}
				
				console.log(httpErrorResponse);
				alert('L\'envoi de l\'enveloppe n\'a pas pu être réalisé à cause d\'un problème technique');
			}
		);
	}
	
	classerSignataires() {
		this.referentielSignataires = {};
		this.signatairesActuels.forEach( signataire => {
			signataire.emailStyle = signataire.email && signataire.email.trim() && applicationConstants.emailRegexp.test(signataire.email) ? '' : 'error';
			if ( signataire.envelopeId ) {
				signataire.envelopeIndex = this.envelopeIds.indexOf(signataire.envelopeId) + 1;
			}
			if (!this.referentielSignataires[signataire.idEntreprise.toString()]) {
				this.referentielSignataires[signataire.idEntreprise] = {
					'nomEntreprise': signataire.nomEntreprise,
					'signataires': [],
					'checkAllForDocusign': signataire.sentToDocusign
				};
			}
			this.referentielSignataires[signataire.idEntreprise.toString()].signataires.push(signataire);
			this.updateAllDocuForEntreprise(signataire.idEntreprise.toString());
		} );
		// tri par nom d'entreprise
		let clonedMap = JSON.parse(JSON.stringify(this.referentielSignataires));
		let sortedKeys = Object.keys(this.referentielSignataires).sort(($1, $2) => {
			return this.referentielSignataires[$1].nomEntreprise.localeCompare(this.referentielSignataires[$2].nomEntreprise);
		});
		this.referentielSignataires = {};
		sortedKeys.forEach(key => {
			this.referentielSignataires[key] = clonedMap[key];
		});
	}
	
	entreprisesSignataireIds(): string[] {
		return Object.keys(this.referentielSignataires).slice().reverse();
	}
	
	viewEnveloppeDetail(envelopeId: string){
		if (envelopeId) {
			let isEnvelopeRetryable = this.pdp.obsolete || this.pdp.expired ? 'false' : 'true';
			this.viewEnveloppeCommand.next(envelopeId + ';' + this.pdp.id + ';' + isEnvelopeRetryable);
		}
	}
	
	shouldSuggestSignatairesDialog() {
		return !this.valideursCanSignThisPdp;
	}
	
	onEnvelopeCancellation() {
		// declencher le refresh de ce popup
		this.signCommandsSubject.next(this.pdp);
	}
}